import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Box,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  updateRemoteNodeCoordinates,
  updateRemoteNodeAddress,
  updateRemoteNodeSubscriber,
  setRemoteNodeFindByAddress,
  setRemoteNodeAddressError,
  clearRemoteNode,
} from '../store/slices/servicePredictorSlice';
import { geocodingService } from '../services/geocoding';
import { validationUtils } from '../utils/validation';
import {
  VALIDATION_MESSAGES,
  COORDINATE_LIMITS,
} from '../constants/validationConstants';
import {
  REMOTE_NODE_INPUT_MODES,
  FORM_DEFAULTS,
} from '../constants/uiConstants';
import { COORDINATE_PRECISION } from '../constants/geographicConstants';
import { ERROR_MESSAGES } from '../constants/errorConstants';

export interface RemoteNodeUpdateData {
  address?: string;
  latitude?: number;
  longitude?: number;
  coordinates?: { lat: number; lng: number };
  subscriber?: string;
  heights?: {
    above_open?: number;
    above_roof?: number;
  };
  clicked?: boolean;
  radius?: boolean;
}

interface RemoteNodeFormProps {
  onClearRNMarker: () => void;
  onUpdateFields?: (data: RemoteNodeUpdateData) => void;
}

interface RemoteNodeFormData {
  subscriber: string;
  address: string;
  latitude: string;
  longitude: string;
  findByAddress: boolean;
}

export type RemoteNodeFormRef = {
  getJsonData: () => any;
  isFormValid: () => boolean;
  clearInputs: () => void;
};

const RemoteNodeForm = forwardRef<RemoteNodeFormRef, RemoteNodeFormProps>(
  ({ onClearRNMarker, onUpdateFields }, ref) => {
    const dispatch = useAppDispatch();
    const remoteNode = useAppSelector(
      (state) => state.servicePredictor.remoteNode
    );
    const [isGeocoding, setIsGeocoding] = useState(false);

    const {
      control,
      watch,
      setValue,
      formState: { errors, isValid },
      reset,
    } = useForm<RemoteNodeFormData>({
      defaultValues: {
        subscriber: remoteNode.subscriber,
        address: remoteNode.address,
        latitude: remoteNode.latitude,
        longitude: remoteNode.longitude,
        findByAddress: remoteNode.findByAddress,
      },
      mode: 'onBlur',
    });

    const findByAddress = watch('findByAddress');
    const address = watch('address');
    const latitude = watch('latitude');
    const longitude = watch('longitude');
    const subscriber = watch('subscriber');

    useEffect(() => {
      reset({
        subscriber: remoteNode.subscriber,
        address: remoteNode.address,
        latitude: remoteNode.latitude,
        longitude: remoteNode.longitude,
        findByAddress: remoteNode.findByAddress,
      });
    }, [
      remoteNode.subscriber,
      remoteNode.address,
      remoteNode.latitude,
      remoteNode.longitude,
      remoteNode.findByAddress,
      reset,
    ]);

    useEffect(() => {
      dispatch(updateRemoteNodeSubscriber(subscriber));
    }, [subscriber, dispatch]);

    useEffect(() => {
      dispatch(setRemoteNodeFindByAddress(findByAddress));
    }, [findByAddress, dispatch]);

    const handleAddressSearch = useCallback(async () => {
      if (!address || address.trim() === '') {
        dispatch(setRemoteNodeAddressError(ERROR_MESSAGES.ADDRESS_REQUIRED));
        onClearRNMarker();
        return;
      }

      // Sanitize address input before API call
      const sanitizedAddress = validationUtils.sanitizeAddress(address);
      if (!sanitizedAddress) {
        dispatch(setRemoteNodeAddressError(ERROR_MESSAGES.ADDRESS_REQUIRED));
        onClearRNMarker();
        return;
      }

      setIsGeocoding(true);
      try {
        const result = await geocodingService.geocodeAddress(sanitizedAddress);
        if (result) {
          const formattedLat =
            result.coordinates.lat.toFixed(COORDINATE_PRECISION);
          const formattedLng =
            result.coordinates.lng.toFixed(COORDINATE_PRECISION);

          setValue('latitude', formattedLat);
          setValue('longitude', formattedLng);
          setValue('address', result.formattedAddress, {
            shouldValidate: true,
          });

          dispatch(
            updateRemoteNodeCoordinates({
              lat: result.coordinates.lat,
              lng: result.coordinates.lng,
            })
          );
          dispatch(updateRemoteNodeAddress(result.formattedAddress));
        } else {
          dispatch(setRemoteNodeAddressError(ERROR_MESSAGES.ADDRESS_NOT_FOUND));
          onClearRNMarker();
        }
      } catch (error: any) {
        dispatch(
          setRemoteNodeAddressError(
            error.message || ERROR_MESSAGES.GEOCODING_FAILED
          )
        );
        onClearRNMarker();
      } finally {
        setIsGeocoding(false);
      }
    }, [address, dispatch, onClearRNMarker, setValue]);

    const handleLatLngBlur = useCallback(async () => {
      const lat = latitude ? parseFloat(latitude) : NaN;
      const lng = longitude ? parseFloat(longitude) : NaN;

      if (!isNaN(lat) && !isNaN(lng)) {
        const formattedLat = lat.toFixed(COORDINATE_PRECISION);
        const formattedLng = lng.toFixed(COORDINATE_PRECISION);

        setValue('latitude', formattedLat);
        setValue('longitude', formattedLng);

        dispatch(updateRemoteNodeCoordinates({ lat, lng }));

        try {
          const result = await geocodingService.reverseGeocode(lat, lng);
          if (result) {
            setValue('address', result.formattedAddress);
            dispatch(updateRemoteNodeAddress(result.formattedAddress));
          }
        } catch (error: any) {
          console.error('Error reverse geocoding:', error);
        }
      }
    }, [latitude, longitude, dispatch, setValue]);

    const validateCoordinates = useCallback(
      (value: string, type: 'latitude' | 'longitude'): boolean | string => {
        if (!findByAddress && !value) {
          return type === 'latitude'
            ? VALIDATION_MESSAGES.LATITUDE_REQUIRED
            : VALIDATION_MESSAGES.LONGITUDE_REQUIRED;
        }
        if (value && !validationUtils.isValidCoordinates(value, type)) {
          return type === 'latitude'
            ? VALIDATION_MESSAGES.INVALID_LATITUDE
            : VALIDATION_MESSAGES.INVALID_LONGITUDE;
        }
        return true;
      },
      [findByAddress]
    );

    const validateAddress = useCallback(
      (value: string): boolean | string => {
        if (findByAddress && !value) {
          return VALIDATION_MESSAGES.ADDRESS_REQUIRED;
        }
        return true;
      },
      [findByAddress]
    );

    const getJsonData = useCallback(() => {
      const rnObject: any = {};

      if (address !== '' && findByAddress) {
        rnObject.address = address;
      }

      if (!findByAddress) {
        if (latitude !== '') {
          rnObject.latitude = parseFloat(latitude);
        }
        if (longitude !== '') {
          rnObject.longitude = parseFloat(longitude);
        }
      }

      if (subscriber !== '') {
        rnObject.subscriber = subscriber;
      }

      const heightsObject: any = {};
      if (remoteNode.aboveOpen !== '') {
        heightsObject.above_open = parseFloat(remoteNode.aboveOpen);
      }
      if (remoteNode.aboveRoof !== '') {
        heightsObject.above_roof = parseFloat(remoteNode.aboveRoof);
      }

      if (Object.keys(heightsObject).length > 0) {
        rnObject.heights = heightsObject;
      }

      return rnObject;
    }, [
      address,
      latitude,
      longitude,
      subscriber,
      findByAddress,
      remoteNode.aboveOpen,
      remoteNode.aboveRoof,
    ]);

    const isFormValid = useCallback((): boolean => {
      if (findByAddress) {
        return address !== '' && isValid;
      }
      return (
        validationUtils.isValidCoordinates(latitude, 'latitude') &&
        validationUtils.isValidCoordinates(longitude, 'longitude')
      );
    }, [findByAddress, address, latitude, longitude, isValid]);

    useImperativeHandle(
      ref,
      () => ({
        getJsonData,
        isFormValid,
        clearInputs: () => {
          reset({
            subscriber: FORM_DEFAULTS.EMPTY_STRING,
            address: FORM_DEFAULTS.EMPTY_STRING,
            latitude: FORM_DEFAULTS.EMPTY_STRING,
            longitude: FORM_DEFAULTS.EMPTY_STRING,
            findByAddress: REMOTE_NODE_INPUT_MODES.ADDRESS,
          });
          dispatch(clearRemoteNode());
          onClearRNMarker();
        },
      }),
      [getJsonData, isFormValid, reset, dispatch, onClearRNMarker]
    );

    const renderInputModeToggle = useCallback(
      () => (
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Controller
            name="findByAddress"
            control={control}
            render={({ field }) => (
              <RadioGroup
                row
                value={field.value.toString()}
                onChange={(e) => {
                  const value = e.target.value === 'true';
                  field.onChange(value);
                  dispatch(setRemoteNodeFindByAddress(value));
                }}
                name="remoteNodeRadioOptions"
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Address"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Lat / Long"
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      ),
      [control, dispatch]
    );

    const renderSubscriberField = useCallback(
      () => (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="subscriber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Subscriber ID / Notes"
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      ),
      [control]
    );

    const renderAddressField = useCallback(
      () => (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="address"
            control={control}
            rules={{
              validate: validateAddress,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Address"
                error={!!errors.address || remoteNode.addressError}
                helperText={
                  errors.address?.message || remoteNode.addressErrorMsg
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddressSearch();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAddressSearch}
                        disabled={isGeocoding}
                        edge="end"
                      >
                        {isGeocoding ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Search />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          {remoteNode.addressErrorMsg && !errors.address && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {remoteNode.addressErrorMsg}
            </Alert>
          )}
        </Box>
      ),
      [
        control,
        errors.address,
        remoteNode.addressError,
        remoteNode.addressErrorMsg,
        validateAddress,
        handleAddressSearch,
        isGeocoding,
      ]
    );

    const renderCoordinatesFields = useCallback(
      () => (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="latitude"
              control={control}
              rules={{
                validate: (value) => validateCoordinates(value, 'latitude'),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Latitude"
                  error={!!errors.latitude}
                  helperText={errors.latitude?.message}
                  onBlur={(e) => {
                    field.onBlur();
                    handleLatLngBlur();
                  }}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              rules={{
                validate: (value) => validateCoordinates(value, 'longitude'),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Longitude"
                  error={!!errors.longitude}
                  helperText={errors.longitude?.message}
                  onBlur={(e) => {
                    field.onBlur();
                    handleLatLngBlur();
                  }}
                />
              )}
            />
          </Box>
        </Box>
      ),
      [
        control,
        errors.latitude,
        errors.longitude,
        validateCoordinates,
        handleLatLngBlur,
      ]
    );

    return (
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}
        >
          Remote Node
        </Typography>

        {renderInputModeToggle()}
        {renderSubscriberField()}
        {findByAddress ? renderAddressField() : renderCoordinatesFields()}
      </Box>
    );
  }
);

RemoteNodeForm.displayName = 'RemoteNodeForm';

export const RemoteNodeFormWithRef = RemoteNodeForm;

export default RemoteNodeForm;
